import * as fs from 'fs';
import * as path from 'path';

export type AgentFramework = 'nextjs' | 'vite' | 'cra' | 'unknown';

const SDK_PACKAGE_NAMES = ['@explorer02/cfm-survey-sdk', '@repo/sdk'] as const;

function readPortFromEnv(): number | undefined {
  const raw = process.env.PORT?.trim();
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function readViteDevPort(projectRoot: string): number {
  const candidates = [
    path.join(projectRoot, 'vite.config.ts'),
    path.join(projectRoot, 'vite.config.js'),
    path.join(projectRoot, 'vite.config.mjs'),
  ];
  for (const filePath of candidates) {
    if (!fs.existsSync(filePath)) continue;
    const source = fs.readFileSync(filePath, 'utf8');
    const match = source.match(/server\s*:\s*\{[^}]*port\s*:\s*(\d+)/s);
    if (match) return Number(match[1]);
  }
  return 5173;
}

/** Default local survey app URL for client handoff after INPUT 2 / Phase 6c. */
export function resolveSurveyDevUrl(projectRoot: string): string {
  const pkgPath = path.join(projectRoot, 'package.json');
  let framework: AgentFramework = 'unknown';
  if (fs.existsSync(pkgPath)) {
    const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf8')) as {
      dependencies?: Record<string, string>;
      devDependencies?: Record<string, string>;
    };
    framework = detectFramework(pkg);
  }

  const portFromEnv = readPortFromEnv();
  if (portFromEnv) return `http://localhost:${portFromEnv}`;

  switch (framework) {
    case 'vite':
      return `http://localhost:${readViteDevPort(projectRoot)}`;
    case 'nextjs':
    case 'cra':
      return 'http://localhost:3000';
    default:
      return 'http://localhost:3000';
  }
}

export function detectFramework(packageJson: {
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
}): AgentFramework {
  const deps = {
    ...packageJson.dependencies,
    ...packageJson.devDependencies,
  };

  if (deps.next) return 'nextjs';
  if (deps.vite) return 'vite';
  if (deps['react-scripts']) return 'cra';
  return 'unknown';
}

export function resolveSdkPackageName(packageJson: {
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
}): string | undefined {
  const deps = {
    ...packageJson.dependencies,
    ...packageJson.devDependencies,
  };

  return SDK_PACKAGE_NAMES.find((name) => Boolean(deps[name]));
}

export function isAwsDeployTarget(surveyUiConfig: { deploy?: { target?: string } }): boolean {
  return surveyUiConfig.deploy?.target === 'aws';
}

export function nextConfigIncludesTranspilePackages(
  nextConfigSource: string,
  sdkPackageName: string,
): boolean {
  return (
    nextConfigSource.includes('transpilePackages') &&
    nextConfigSource.includes(sdkPackageName)
  );
}

export function nextConfigHasStaticExport(nextConfigSource: string): boolean {
  return (
    /output\s*:\s*['"]export['"]/.test(nextConfigSource) &&
    /assetPrefix\s*:\s*['"]\.\/['"]/.test(nextConfigSource)
  );
}

export function viteConfigHasRelativeBase(viteConfigSource: string): boolean {
  return /base\s*:\s*['"]\.\/['"]/.test(viteConfigSource);
}

export function sourceHasNextImports(source: string): boolean {
  return /from\s+['"]next\//.test(source);
}

export function sourceHasUseClientDirective(source: string): boolean {
  return /^['"]use client['"];?\s*$/m.test(source);
}
